import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * MuiDropdown - A className-based implementation that matches the Bulma Dropdown
 * 
 * This component provides the same API as the previous MuiDropdown for easy migration
 */
const MuiDropdown = ({
  children,
  isAlign,
  renderTrigger,
  className,
  ...rest
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  const handleClick = (event) => {
    event.stopPropagation();
    setIsVisible(!isVisible);
  };

  const handleClose = useCallback(() => {
    setIsVisible(false);
  }, []);

  // Add document click listener to close dropdown when clicking outside
  useEffect(() => {
    if (isVisible) {
      document.addEventListener('click', handleClose);
    }

    return () => {
      document.removeEventListener('click', handleClose);
    };
  }, [isVisible, handleClose]);

  // Build the class names based on props
  const dropdownClasses = classNames('dropdown', {
    'is-active': isVisible,
    'is-right': isAlign === 'right',
    [className]: className,
  });

  // Clone the trigger element and add the onClick handler
  const triggerElement = React.cloneElement(
    typeof renderTrigger === 'function' ? renderTrigger() : renderTrigger,
    { onClick: handleClick }
  );

  return (
    <div className={dropdownClasses} {...rest}>
      <div className="dropdown-trigger">
        {triggerElement}
      </div>
      <div className="dropdown-menu" role="menu">
        <div className="dropdown-content">
          {children}
        </div>
      </div>
    </div>
  );
};

MuiDropdown.propTypes = {
  children: PropTypes.node.isRequired,
  isAlign: PropTypes.string,
  renderTrigger: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  className: PropTypes.string,
};

MuiDropdown.defaultProps = {
  isAlign: 'left',
  className: '',
};

export default MuiDropdown;
